import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import testCases from './ecommerceProduct/testCases.json';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Container, Row, Col, Form, Button, Card, Alert, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../cssComponents/PracticeLoginPage.css';
import HeroImage from '../icons/form-icon.png';
import TesterBudNavbar from '../../subComponent/TesterBudNavBar';

const AutomateLoginPages = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [collapseStates, setCollapseStates] = useState({});

  // Predefined credentials
  const correctEmail = 'user@premiumbank.com';
  const correctPassword = 'Bank@123';

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email && !password) {
      setError('Email and Password are required');
      return;
    }

    if (email && !password) {
      setError('Password is required');
      return;
    }

    if (!email && password) {
      setError('Email is required');
      return;
    }

    if (email !== correctEmail || password !== correctPassword) {
      setError('Invalid email id and password');
      return;
    }

    setSuccess(true);
  };

  const handleGenerateTestCases = () => {
    setModalOpen(true);
  };

  const toggleCollapse = (id) => {
    setCollapseStates({ ...collapseStates, [id]: !collapseStates[id] });
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Login Automation Practice Page | Selenium, Cypress, Playwright</title>
        <meta http-equiv="Content-type" content="text/html;charset=UTF-8"></meta>
        <meta name="description" content="Enhance your software testing skills with our free dummy login page. Practice automating login forms using Selenium, Cypress, and Playwright. Ideal for beginners learning UI automation and test automation." />
        <meta name="keywords" content="login automation practice, selenium login practice, cypress login automation, playwright login testing, dummy login page, test automation practice, ui automation examples, login form automation, web login automation, practice website login, free test automation practice, selenium practice login form, automate login functionality" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="TesterBud Team" />
        <meta property="og:title" content="Login Automation Practice Page | Selenium, Cypress, Playwright" />
        <meta property="og:description" content="Enhance your software testing skills with our free dummy login page. Practice automating login forms using Selenium, Cypress, and Playwright. Ideal for beginners learning UI automation and test automation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://testerbud.com/practice-login-automation" />
        <meta name="geo.region" content="GLOBAL" />
        <meta name="geo.placename" content="Global" />
      </Helmet>

      <Container fluid className="hero-section bg-primary text-white py-5" style={{ position: 'relative', overflow: 'hidden' }}>
        <Row className="align-items-center justify-content-center">
          <Col md={6} className="text-center text-md-start">
            <h1 className="display-4 fw-bold mb-3">Master Login Automation with TesterBud</h1>
            <p className="lead mb-4">
              Hone your software testing expertise by practicing login form automation on our free, interactive dummy login page. Perfect for Selenium, Cypress, and Playwright learners.
            </p>
            <p className="lead mb-4">
              Develop hands-on skills, create robust test cases, and become a confident UI automation specialist. 🚀
            </p>
          </Col>
          <Col md={5} className="d-none d-md-block text-center">
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: '50%',
                  height: '50%',
                  borderRadius: '0%',
                  overflow: 'auto',
                  marginTop: '-4%',
                  marginRight: '10%',
                  marginBottom: '-4%',
                }}
              >
                <img src={HeroImage} alt="Software Testing Automation Illustration" className="img-fluid" style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }} />
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <TesterBudNavbar />

      <Container className="mt-10 pt-5 pb-5 mb-8" style={{ maxWidth: '90%' }}>
        
            <Card className="shadow-lg rounded-lg p-5 bg-light">
              <Card.Body>
                <h2 className="card-title mb-4">Why Practice Login Automation?</h2>
                <p className="card-text mb-4">
                  In the world of software testing, mastering login automation is crucial. This practice login page is designed to empower you to <strong>develop essential UI automation skills</strong>. By simulating a real-world login scenario, you'll gain hands-on experience with industry-leading tools such as <strong>Selenium, Cypress,</strong> and <strong>Playwright</strong>.
                </p>
                <p className="card-text mb-4">
                  Here's how this practice page helps you:
                </p>
                <ul className="list-group list-group-flush mb-4">
                  <li className="list-group-item"><strong>Skill Enhancement:</strong>  Become proficient in handling various login elements, including input fields, buttons, and error messages.</li>
                  <li className="list-group-item"><strong>Scenario Mastery:</strong>  Learn to automate a wide range of login scenarios, from basic successful logins to handling incorrect credentials and password recovery.</li>
                  <li className="list-group-item"><strong>Tool Proficiency:</strong>  Gain practical experience with Selenium, Cypress, and Playwright, building a strong foundation for your automation career.</li>
                  <li className="list-group-item"><strong>Best Practices:</strong> Implement robust and reliable automation techniques.</li>
                </ul>
                <p className="card-text mb-4">
                  Use the provided credentials to practice and improve your test automation skills. This is your stepping stone to becoming a confident and effective UI automation expert.
                </p>
                <p className="card-text">
                  This page provides a safe environment to practice and improve your skills.
                </p>
              </Card.Body>
            </Card>
          
      </Container>


      <Container className="mt-5 pt-5 pb-5 mb-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="shadow-lg rounded-lg p-4">
              <Card.Body>
                <div className="text-center mb-4">
                  <h2>Login to Your Practice Account</h2>
                  <p className="text-muted">Practice UI Automation for Secure Banking</p>
                </div>

                <Alert variant="info" className="mb-3">
                  <h6 className="mb-2">Demo Credentials for Login Automation:</h6>
                  <p className="mb-0">
                    <strong>Email:</strong> {correctEmail}
                  </p>
                  <p className="mb-0">
                    <strong>Password:</strong> {correctPassword}
                  </p>
                  <small className="text-muted">Use these credentials to practice automating the login process.</small>
                </Alert>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">Login Successful! Welcome to Premium Banking.</Alert>}

                {!success && (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-control-lg"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicPassword">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-control-lg"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicCheckbox">
                      <Form.Check type="checkbox" label="Remember me" />
                    </Form.Group>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Link to="/forget-password">Forgot password?</Link>
                    </div>

                    <Button variant="primary" type="submit" className="btn-lg w-100">
                      Sign in
                    </Button>
                  </Form>
                )}

                <div className="text-center mt-3">
                  Don't have an account? <Link to="/register">Register now</Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="text-center mt-4">
          <Button variant="outline-secondary" size="lg" onClick={handleGenerateTestCases}>
            Generate Test Cases for Login Page 🔂
          </Button>
          <p className="mt-2 text-muted">
            Explore common test scenarios for login functionality to enhance your automation practice.
          </p>
        </div>

        <div className="text-center mt-3">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Button variant="outline-info" size="lg">Back To Practice UI Automation Examples ➡️</Button>
          </Link>
        </div>

        <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg" centered scrollable>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>🧪 Test Cases for Login Page</Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {Object.keys(testCases.test_cases).map((category) => (
              <div key={category} className="mb-3">
                <Button
                  variant="light"
                  className="w-100 text-start fw-bold border d-flex justify-content-between align-items-center"
                  onClick={() => toggleCollapse(category)}
                  aria-controls={`collapse-${category}`}
                  aria-expanded={collapseStates[category]}
                >
                  <span>{category}</span>
                  <span>{collapseStates[category] ? '▲' : '▼'}</span>
                </Button>

                <div id={`collapse-${category}`} className={`collapse ${collapseStates[category] ? 'show' : ''}`}>
                  <Card className="mt-2 border-0 shadow-sm">
                    <Card.Body>
                      {testCases.test_cases[category].map((testCase) => (
                        <div key={testCase.id} className="mb-4">
                          <h6 className="fw-semibold mb-2">{testCase.id}: {testCase.description}</h6>
                          <ul className="ps-4">
                            {testCase.steps.map((step, index) => (
                              <li key={index} className="mb-1">{step}</li>
                            ))}
                          </ul>
                          <p className="text-success"><strong>Expected:</strong> {testCase.expected_result}</p>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ))}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setModalOpen(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>

      <footer className="bg-dark text-light text-center py-4 mt-auto">
        <Container>
          <p className="mb-1">Ready to start automating? Explore, practice, and master!</p>
          <small>© 2025 TesterBud | Built for learners by learners 🚀</small>
        </Container>
      </footer>
    </HelmetProvider>
  );
};

export default AutomateLoginPages;
